import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import { useEffect, useRef } from 'react';
import {
  useQuizMetaContext,
  useQuizResults,
  useFollowerQuizGameState,
} from '@mentimeter/quiz';
import { TitleSection } from '@mentimeter/question-modules-shared';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import { GameStateEnums } from '@mentimeter/quiz-types';
import { RevealAnswer } from '../../shared/components/quiz/RevealAnswer';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Lobby } from '../../shared/components/quiz/lobby';
import { GetReady } from '../../shared/components/quiz/get-ready';
import { Countdown } from '../../shared/components/quiz/countdown';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { Results } from './results/Results';

export const FollowerPresentation = ({
  activeSession,
  correctAnswer,
}: {
  activeSession: number;
  correctAnswer: string | undefined;
}) => {
  const { useQuestion, useTranslate } = usePresentationContext();
  const {
    image,
    time_based_scoring,
    question,
    title_meta,
    question_description,
    question_styled,
    layout,
  } = useQuestion();
  const translationFn = useTranslate();
  const { totalCount, currentIndex, follower } = useQuizMetaContext();
  const { data = [], isPreviewResults } =
    useQuizResults<QuizPlayersScoreData>();
  const {
    gameState,
    timeOffset = 0,
    connectedPlayers,
    respondents,
    joinAsFollower,
    connectedWithDelay,
  } = useFollowerQuizGameState();

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      joinAsFollower();
    }
  }, [joinAsFollower, follower, activeSession, gameState]);

  const shouldShowLegacyImage = layout?.type === 'default';

  if (isPreviewResults) {
    return (
      <>
        <TitleSection
          title={question}
          styledTitle={question_styled}
          label={title_meta}
          fontSizeOffset={layout?.font_size_offset}
        />
        {data.length > 0 && (
          <RevealAnswer correctAnswer={correctAnswer}>
            <Results results={data} isReadonly translationFn={translationFn} />
          </RevealAnswer>
        )}
      </>
    );
  }

  switch (gameState?.gameState) {
    case GameStateEnums.LOBBY: {
      return (
        <CenteringContainer data-testid="gamestate-lobby">
          <Lobby
            connected={connectedWithDelay}
            total={totalCount}
            current={currentIndex + 1}
            players={connectedPlayers}
          />
        </CenteringContainer>
      );
    }
    case GameStateEnums.GET_READY: {
      return (
        <CenteringContainer>
          <GetReady timeBasedScoring={time_based_scoring} />
        </CenteringContainer>
      );
    }
    case GameStateEnums.COUNTDOWN: {
      return (
        <>
          <TitleSection
            title={question}
            styledTitle={question_styled}
            label={title_meta}
            description={question_description}
            fontSizeOffset={layout?.font_size_offset}
          />
          <CenteringContainer>
            <Countdown
              type="open"
              respondents={respondents.length}
              everyoneHasVoted={false}
              imageUrl={
                image?.presets?.original?.url ?? image?.presets?.large?.url
              }
              timeOffset={timeOffset}
              startAt={gameState.gameStateData.startAt}
              endAt={gameState.gameStateData.endAt}
              shouldShowLegacyImage={shouldShowLegacyImage}
            />
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.RESULT: {
      return (
        <>
          <TitleSection
            title={question}
            styledTitle={question_styled}
            label={title_meta}
            fontSizeOffset={layout?.font_size_offset}
          />
          <RevealAnswer correctAnswer={correctAnswer}>
            <Results results={data} translationFn={translationFn} isReadonly />
          </RevealAnswer>
        </>
      );
    }
    case GameStateEnums.ERROR: {
      return (
        <CenteringContainer>
          <Error errorCode={gameState.errorCode} />
        </CenteringContainer>
      );
    }
    default: {
      return (
        <CenteringContainer>
          <Loading
            onReload={() => {
              window.location.reload();
            }}
          />
        </CenteringContainer>
      );
    }
  }
};
